import {
	Badge,
	Box,
	Button,
	Card,
	Flex,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { ApexOptions } from 'apexcharts'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { getCommunityStats } from 'src/apis/chartAPI'
import CustomDatePickerInput from 'src/components/CustomComponents/CustomDatePickerInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import { useModal } from 'src/context/ModalContext'
import { color, formatDate } from 'src/variable/constant'

type Granularity = 'day' | 'hour'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const defaultStartDate = dayjs().subtract(6, 'days').toDate()
const defaultEndDate = new Date()

const CHART_NOTES = {
	messageVolume:
		'Shows overall community activity and how strongly users are engaging through conversations.',
	activeConversations:
		'Shows rooms that are actually alive through last_time_chat, making it a real engagement and retention signal.',
	kindDistribution:
		'Compares which conversation kinds create real conversations and messages to reveal feature adoption and high-value use cases.',
	responseTime:
		'Measures conversation quality and how likely users are to receive replies after they participate.',
	peakHour:
		'Highlights the best activity windows for campaigns, content planning, and community operations.',
}

const INSIGHT_LEVEL_COLOR = {
	neutral: 'gray',
	good: 'green',
	warning: 'orange',
	critical: 'red',
}

const CONVERSATION_KIND_LABELS = {
	PERSONAL: 'INBOX 1-1',
	CHAT_ROOM: 'CHAT ROOM',
	MINI_CHAT: 'MINI CHAT',
	CHAT_LOCATION: 'CHAT LOCATION',
}

const emptyStats = {
	filters: {},
	summary: {
		total_messages: 0,
		total_active_conversations: 0,
		total_conversations: 0,
		response_samples: 0,
	},
	message_volume: [],
	active_conversations: [],
	kind_distribution: [],
	peak_hour_heatmap: [],
	response_time: [],
}

function numberFormat(value: any) {
	return Number(value || 0).toLocaleString('en-US')
}

function secondsFormat(value: any) {
	const seconds = Number(value || 0)
	if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)}h`
	if (seconds >= 60) return `${(seconds / 60).toFixed(1)}m`
	return `${seconds.toFixed(0)}s`
}

function percentFormat(value: number) {
	return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
}

function conversationKindFormat(kind: any) {
	const kindKey = String(kind || '')
	return CONVERSATION_KIND_LABELS[kindKey] || kindKey
}

function uniqueValues(items: any[], key: string) {
	return Array.from(new Set(items.map((item) => item[key]).filter(Boolean)))
}

function buildGroupedSeries(
	items: any[],
	xKey: string,
	groupKey: string,
	valueKey: string,
) {
	const categories = uniqueValues(items, xKey).sort()
	const groups = uniqueValues(items, groupKey).sort()

	return {
		categories,
		series: groups.map((group) => ({
			name: conversationKindFormat(group),
			data: categories.map((category) => {
				const item = items.find(
					(row) => row[xKey] === category && row[groupKey] === group,
				)
				return Number(item?.[valueKey] || 0)
			}),
		})),
	}
}

function getTotalByBucket(items: any[], bucketKey: string, valueKey: string) {
	const bucketMap = items.reduce((result, item) => {
		const bucket = item[bucketKey]
		result[bucket] = (result[bucket] || 0) + Number(item[valueKey] || 0)
		return result
	}, {})

	return Object.keys(bucketMap)
		.sort()
		.map((bucket) => ({
			bucket,
			value: bucketMap[bucket],
		}))
}

function getChangePercent(current: number, previous: number) {
	if (!previous) return current > 0 ? 100 : 0
	return ((current - previous) / previous) * 100
}

function getMessageVolumeInsight(items: any[]) {
	const buckets = getTotalByBucket(items || [], 'bucket', 'message_count')
	if (!buckets.length) {
		return {
			level: 'neutral',
			text: 'No message traffic in this range.',
		}
	}

	const last = buckets[buckets.length - 1]
	const previous = buckets[buckets.length - 2]
	if (!previous) {
		return {
			level: 'neutral',
			text: `${numberFormat(last.value)} messages recorded in this range.`,
		}
	}

	const change = getChangePercent(last.value, previous.value)
	if (change <= -50) {
		return {
			level: 'critical',
			text: `Latest bucket dropped ${percentFormat(change)} versus the previous bucket. User activity is cooling down in this range.`,
		}
	}
	if (change >= 50) {
		return {
			level: 'warning',
			text: `Latest bucket grew ${percentFormat(change)} versus the previous bucket. User activity is accelerating and may indicate a high-interest moment.`,
		}
	}

	return {
		level: 'good',
		text: `User activity is stable in the latest bucket (${percentFormat(change)} versus previous).`,
	}
}

function getActiveConversationInsight(items: any[]) {
	const buckets = getTotalByBucket(items || [], 'bucket', 'active_count')
	if (!buckets.length) {
		return {
			level: 'neutral',
			text: 'No active conversations in this range.',
		}
	}

	const last = buckets[buckets.length - 1]
	const previous = buckets[buckets.length - 2]
	if (!last.value) {
		return {
			level: 'critical',
			text: 'Latest bucket has no active rooms. Users are not returning to conversations in this period.',
		}
	}
	if (!previous) {
		return {
			level: 'neutral',
			text: `${numberFormat(last.value)} active conversations recorded in the latest bucket.`,
		}
	}

	const change = getChangePercent(last.value, previous.value)
	if (change <= -30) {
		return {
			level: 'warning',
			text: `Active conversations fell ${percentFormat(change)} in the latest bucket. Room engagement is decreasing.`,
		}
	}
	if (change >= 30) {
		return {
			level: 'good',
			text: `Active conversations grew ${percentFormat(change)} in the latest bucket. Room engagement is increasing.`,
		}
	}

	return {
		level: 'good',
		text: `Active rooms are steady (${percentFormat(change)} versus previous bucket).`,
	}
}

function getKindDistributionInsight(items: any[]) {
	const data = [...(items || [])].sort(
		(a, b) => Number(b.message_count || 0) - Number(a.message_count || 0),
	)
	if (!data.length) {
		return {
			level: 'neutral',
			text: 'No conversation kind distribution in this range.',
		}
	}

	const topMessageKind = data[0]
	const conversationHeavyKind = [...data].sort(
		(a, b) =>
			Number(b.conversation_percent || 0) -
			Number(a.conversation_percent || 0),
	)[0]

	if (
		Number(topMessageKind.message_percent || 0) >= 60 &&
		Number(topMessageKind.conversation_percent || 0) <= 35
	) {
		return {
			level: 'good',
			text: `${conversationKindFormat(topMessageKind.kind)} drives ${Number(topMessageKind.message_percent || 0).toFixed(1)}% of messages from only ${Number(topMessageKind.conversation_percent || 0).toFixed(1)}% of conversations. This kind is highly engaged.`,
		}
	}

	if (
		conversationHeavyKind &&
		Number(conversationHeavyKind.conversation_percent || 0) >= 45 &&
		Number(conversationHeavyKind.message_percent || 0) <= 20
	) {
		return {
			level: 'warning',
			text: `${conversationKindFormat(conversationHeavyKind.kind)} owns many conversations but low message share. Watch for fake growth or weak adoption.`,
		}
	}

	return {
		level: 'neutral',
		text: `${conversationKindFormat(topMessageKind.kind)} is the top message kind with ${Number(topMessageKind.message_percent || 0).toFixed(1)}% of messages.`,
	}
}

function getResponseTimeInsight(items: any[]) {
	const data = items || []
	const totalSamples = data.reduce(
		(sum, item) => sum + Number(item.sample_count || 0),
		0,
	)
	if (totalSamples < 5) {
		return {
			level: 'neutral',
			text: 'Not enough reply-turn data to evaluate response quality.',
		}
	}

	const worst = [...data].sort(
		(a, b) => Number(b.p95_seconds || 0) - Number(a.p95_seconds || 0),
	)[0]
	const median = Number(worst?.median_seconds || 0)
	const p95 = Number(worst?.p95_seconds || 0)

	if (p95 >= 600) {
		return {
			level: 'warning',
			text: `${conversationKindFormat(worst.kind)} has p95 response time at ${secondsFormat(p95)}. Conversation experience is slow for some users.`,
		}
	}
	if (median <= 60 && p95 >= 300) {
		return {
			level: 'warning',
			text: `${conversationKindFormat(worst.kind)} median is healthy but p95 is ${secondsFormat(p95)}. Tail response time needs attention.`,
		}
	}

	return {
		level: 'good',
		text: `Conversation experience looks healthy. Worst p95 is ${secondsFormat(p95)} for ${conversationKindFormat(worst.kind)}.`,
	}
}

function getPeakHourInsight(items: any[]) {
	const rows = items || []
	const peak = [...rows].sort(
		(a, b) => Number(b.messages || 0) - Number(a.messages || 0),
	)[0]
	if (!peak || Number(peak.messages || 0) === 0) {
		return {
			level: 'neutral',
			text: 'Not enough message data to identify a peak activity window.',
		}
	}

	const weekday = WEEKDAY_LABELS[Number(peak.weekday || 1) - 1] || 'Unknown'
	return {
		level: 'good',
		text: `Peak activity is ${weekday} at ${peak.hour}:00 with ${numberFormat(peak.messages)} messages. Use this window for campaigns, content, and community operations.`,
	}
}

function ChartHeader({
	title,
	note,
	insight,
	rightSection,
}: {
	title: string
	note: string
	insight?: {
		level: string
		text: string
	}
	rightSection?: React.ReactNode
}) {
	return (
		<Flex justify="space-between" align="flex-start" gap={16} mb="sm">
			<Stack spacing={2} sx={{ minWidth: 0 }}>
				<Text fw={700}>{title}</Text>
				<Text color="dimmed" size="sm">
					{note}
				</Text>
				{insight && (
					<Flex gap={8} align="center" wrap="wrap">
						<Badge
							size="sm"
							variant="light"
							color={INSIGHT_LEVEL_COLOR[insight.level] || 'gray'}
						>
							Insight
						</Badge>
						<Text
							size="sm"
							color={INSIGHT_LEVEL_COLOR[insight.level] || 'dimmed'}
						>
							{insight.text}
						</Text>
					</Flex>
				)}
			</Stack>
			{rightSection}
		</Flex>
	)
}

function CommunityStatsPage() {
	const { openError } = useModal()
	const [loading, setLoading] = useState(false)
	const [startDate, setStartDate] = useState<Date | null>(defaultStartDate)
	const [endDate, setEndDate] = useState<Date | null>(defaultEndDate)
	const [granularity, setGranularity] = useState<Granularity>('day')
	const [requestParams, setRequestParams] = useState({
		start_date: dayjs(defaultStartDate).format('YYYY-MM-DD'),
		end_date: dayjs(defaultEndDate).format('YYYY-MM-DD'),
		granularity: 'day' as Granularity,
		request_id: 0,
	})
	const [stats, setStats] = useState<any>(emptyStats)

	const handleGetStats = useCallback(async () => {
		setLoading(true)
		try {
			const res = await getCommunityStats({
				start_date: requestParams.start_date,
				end_date: requestParams.end_date,
				granularity: requestParams.granularity,
			})
			setStats(res?.data?.results?.object || emptyStats)
		} catch (error) {
			setStats(emptyStats)
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [openError, requestParams])

	const handleRefresh = useCallback(() => {
		if (!startDate || !endDate) return
		if (dayjs(startDate).isAfter(dayjs(endDate), 'day')) {
			openError({ message: 'Start date must be before end date' })
			return
		}

		setRequestParams((prev) => ({
			...prev,
			start_date: dayjs(startDate).format('YYYY-MM-DD'),
			end_date: dayjs(endDate).format('YYYY-MM-DD'),
			granularity,
			request_id: prev.request_id + 1,
		}))
	}, [endDate, granularity, openError, startDate])

	const appliedGranularity = (stats.filters?.granularity ||
		requestParams.granularity) as Granularity

	const isPendingFilter =
		startDate &&
		endDate &&
		(requestParams.start_date !== dayjs(startDate).format('YYYY-MM-DD') ||
			requestParams.end_date !== dayjs(endDate).format('YYYY-MM-DD') ||
			requestParams.granularity !== granularity)

	useEffect(() => {
		handleGetStats()
	}, [handleGetStats])

	const messageVolumeData = useMemo(
		() =>
			buildGroupedSeries(
				stats.message_volume || [],
				'bucket',
				'kind',
				'message_count',
			),
		[stats.message_volume],
	)

	const messageVolumeInsight = useMemo(
		() => getMessageVolumeInsight(stats.message_volume || []),
		[stats.message_volume],
	)

	const activeConversationData = useMemo(
		() =>
			buildGroupedSeries(
				stats.active_conversations || [],
				'bucket',
				'kind',
				'active_count',
			),
		[stats.active_conversations],
	)

	const activeConversationInsight = useMemo(
		() => getActiveConversationInsight(stats.active_conversations || []),
		[stats.active_conversations],
	)

	const messageVolumeOptions = useMemo<ApexOptions>(
		() => ({
			chart: {
				type: 'area',
				stacked: true,
				toolbar: { show: true, tools: { download: false } },
			},
			dataLabels: { enabled: false },
			stroke: { curve: 'smooth', width: 2 },
			xaxis: {
				categories: messageVolumeData.categories,
				labels: { rotate: -35, trim: true },
			},
			yaxis: { min: 0 },
			tooltip: {
				y: { formatter: (value) => numberFormat(value) },
			},
			colors: [color.CmsPrimary, color.Blue, color.Orange, color.BlueV2, '#8B5CF6', '#14B8A6'],
			legend: { position: 'top' },
		}),
		[messageVolumeData.categories],
	)

	const activeConversationOptions = useMemo<ApexOptions>(
		() => ({
			chart: {
				type: 'line',
				toolbar: { show: true, tools: { download: false } },
			},
			dataLabels: { enabled: false },
			stroke: { curve: 'smooth', width: 3 },
			xaxis: {
				categories: activeConversationData.categories,
				labels: { rotate: -35, trim: true },
			},
			yaxis: { min: 0 },
			tooltip: {
				y: { formatter: (value) => numberFormat(value) },
			},
			colors: [color.Blue, color.CmsPrimary, color.Orange, color.BlueV2, '#8B5CF6', '#14B8A6'],
			legend: { position: 'top' },
		}),
		[activeConversationData.categories],
	)

	const distributionItems = useMemo(
		() =>
			[...(stats.kind_distribution || [])].sort((a, b) => {
				const messageDiff =
					Number(b.message_percent || 0) - Number(a.message_percent || 0)
				if (messageDiff) return messageDiff

				return (
					Number(b.conversation_percent || 0) -
					Number(a.conversation_percent || 0)
				)
			}),
		[stats.kind_distribution],
	)

	const distributionInsight = useMemo(
		() => getKindDistributionInsight(distributionItems),
		[distributionItems],
	)

	const topMessageKind = distributionItems[0]
	const topConversationKind = useMemo(
		() =>
			[...distributionItems].sort(
				(a, b) =>
					Number(b.conversation_percent || 0) -
					Number(a.conversation_percent || 0),
			)[0],
		[distributionItems],
	)

	const distributionChartHeight = Math.max(
		380,
		distributionItems.length * 48 + 120,
	)

	const distributionOptions = useMemo<ApexOptions>(
		() => ({
			chart: {
				type: 'bar',
				stacked: false,
				toolbar: { show: true, tools: { download: false } },
				animations: { enabled: false },
			},
			plotOptions: {
				bar: {
					horizontal: true,
					borderRadius: 5,
					barHeight: '58%',
					dataLabels: { position: 'top' },
				},
			},
			dataLabels: {
				enabled: true,
				offsetX: 18,
				formatter: (value) => {
					const percent = Number(value || 0)
					if (percent < 3) return ''
					return `${percent.toFixed(1)}%`
				},
				style: {
					colors: [color.TextPrimary],
					fontSize: '11px',
					fontWeight: 700,
				},
				background: {
					enabled: false,
				},
			},
			xaxis: {
				categories: distributionItems.map((item) =>
					conversationKindFormat(item.kind),
				),
				min: 0,
				max: 100,
				tickAmount: 5,
				labels: {
					formatter: (value) => `${Number(value || 0).toFixed(0)}%`,
					style: { colors: '#6B7280', fontSize: '12px' },
				},
				axisBorder: { show: false },
				axisTicks: { show: false },
			},
			yaxis: {
				labels: {
					maxWidth: 132,
					style: {
						colors: color.TextPrimary,
						fontSize: '12px',
						fontWeight: 600,
					},
				},
			},
			grid: {
				borderColor: '#E5E7EB',
				strokeDashArray: 4,
				xaxis: { lines: { show: true } },
				yaxis: { lines: { show: false } },
			},
			tooltip: {
				y: { formatter: (value) => `${Number(value || 0).toFixed(2)}%` },
			},
			colors: [color.CmsPrimary, color.Orange],
			legend: {
				position: 'top',
				horizontalAlign: 'left',
				offsetY: 4,
				itemMargin: { horizontal: 16, vertical: 8 },
			},
			states: {
				hover: { filter: { type: 'lighten', value: 0.04 } },
				active: { filter: { type: 'none' } },
			},
		}),
		[distributionItems],
	)

	const distributionSeries = useMemo(
		() => [
			{
				name: '% Conversations',
				data: distributionItems.map((item) =>
					Number(item.conversation_percent || 0),
				),
			},
			{
				name: '% Messages',
				data: distributionItems.map((item) =>
					Number(item.message_percent || 0),
				),
			},
		],
		[distributionItems],
	)

	const heatmapSeries = useMemo(() => {
		const rows = stats.peak_hour_heatmap || []
		return WEEKDAY_LABELS.map((label, index) => ({
			name: label,
			data: Array.from({ length: 24 }, (_, hour) => {
				const item = rows.find(
					(row) => Number(row.weekday) === index + 1 && Number(row.hour) === hour,
				)
				return {
					x: `${hour}:00`,
					y: Number(item?.messages || 0),
					active_conversations: Number(item?.active_conversations || 0),
				}
			}),
		}))
	}, [stats.peak_hour_heatmap])

	const heatmapInsight = useMemo(
		() => getPeakHourInsight(stats.peak_hour_heatmap || []),
		[stats.peak_hour_heatmap],
	)

	const heatmapOptions = useMemo<ApexOptions>(
		() => ({
			chart: {
				type: 'heatmap',
				toolbar: { show: true, tools: { download: false } },
			},
			dataLabels: { enabled: false },
			plotOptions: {
				heatmap: {
					shadeIntensity: 0.45,
					colorScale: {
						ranges: [
							{ from: 0, to: 0, color: '#F1F5F9', name: '0' },
							{ from: 1, to: 20, color: '#93C5FD', name: 'Low' },
							{ from: 21, to: 100, color: color.CmsPrimary, name: 'Mid' },
							{ from: 101, to: 1000000, color: color.Orange, name: 'High' },
						],
					},
				},
			},
			tooltip: {
				custom: ({ seriesIndex, dataPointIndex, w }) => {
					const point =
						w.config.series?.[seriesIndex]?.data?.[dataPointIndex] || {}
					return `<div style="padding:8px 10px">
						<div>${WEEKDAY_LABELS[seriesIndex]} ${point.x}</div>
						<div>Messages: ${numberFormat(point.y)}</div>
						<div>Active conversations: ${numberFormat(point.active_conversations)}</div>
					</div>`
				},
			},
		}),
		[],
	)

	const responseItems = useMemo(
		() => stats.response_time || [],
		[stats.response_time],
	)

	const responseInsight = useMemo(
		() => getResponseTimeInsight(responseItems),
		[responseItems],
	)

	const responseOptions = useMemo<ApexOptions>(
		() => ({
			chart: {
				type: 'bar',
				toolbar: { show: true, tools: { download: false } },
			},
			plotOptions: {
				bar: { borderRadius: 4, columnWidth: '46%' },
			},
			dataLabels: { enabled: false },
			xaxis: {
				categories: responseItems.map((item) =>
					conversationKindFormat(item.kind),
				),
			},
			yaxis: {
				min: 0,
				labels: { formatter: (value) => secondsFormat(value) },
			},
			tooltip: {
				y: { formatter: (value) => secondsFormat(value) },
			},
			colors: [color.CmsPrimary, color.Orange],
			legend: { position: 'top' },
		}),
		[responseItems],
	)

	const responseSeries = useMemo(
		() => [
			{
				name: 'Median',
				data: responseItems.map((item) => Number(item.median_seconds || 0)),
			},
			{
				name: 'P95',
				data: responseItems.map((item) => Number(item.p95_seconds || 0)),
			},
		],
		[responseItems],
	)

	return (
		<Stack sx={{ width: '100%' }} spacing="md">
			<Flex align="flex-end" gap={16} wrap="wrap">
				<Stack spacing={4} sx={{ minWidth: 260 }}>
					<Text fw={700} size="lg">
						Community Stats
					</Text>
					<Text color="dimmed" size="sm">
						Message and conversation health metrics
					</Text>
				</Stack>
				<Flex
					gap={12}
					align="flex-end"
					wrap="wrap"
					sx={{ marginLeft: 'auto' }}
				>
					<Box sx={{ width: 180, flex: '0 0 auto' }}>
						<CustomDatePickerInput
							label="Start date"
							placeholder="DD/MM/YYYY"
							valueFormat={formatDate}
							value={startDate}
							maxDate={endDate || new Date()}
							onChange={setStartDate}
						/>
					</Box>
					<Box sx={{ width: 180, flex: '0 0 auto' }}>
						<CustomDatePickerInput
							label="End date"
							placeholder="DD/MM/YYYY"
							valueFormat={formatDate}
							value={endDate}
							minDate={startDate || undefined}
							maxDate={new Date()}
							onChange={setEndDate}
						/>
					</Box>
					<Box sx={{ width: 160, flex: '0 0 auto' }}>
						<CustomSelect
							label="Granularity"
							value={granularity}
							onChange={(value) => setGranularity((value || 'day') as Granularity)}
							data={[
								{ value: 'day', label: 'Daily' },
								{ value: 'hour', label: 'Hourly' },
							]}
							sx={{ minWidth: 160, width: 160 }}
						/>
					</Box>
					<Box sx={{ flex: '0 0 auto' }}>
						<Button
							variant="outline"
							leftIcon={<IconRefresh size={16} />}
							onClick={handleRefresh}
							loading={loading}
						>
							{isPendingFilter ? 'Apply' : 'Refresh'}
						</Button>
					</Box>
				</Flex>
			</Flex>

			<SimpleGrid
				cols={4}
				spacing="md"
				breakpoints={[
					{ maxWidth: 1200, cols: 2 },
					{ maxWidth: 520, cols: 1 },
				]}
			>
				<Card withBorder radius="md" p="md">
					<Stack spacing={4}>
						<Text size="sm" color="dimmed">
							Messages
						</Text>
						<Title order={3}>
							{numberFormat(stats.summary?.total_messages)}
						</Title>
					</Stack>
				</Card>
				<Card withBorder radius="md" p="md">
					<Stack spacing={4}>
						<Text size="sm" color="dimmed">
							Active conversations
						</Text>
						<Title order={3}>
							{numberFormat(stats.summary?.total_active_conversations)}
						</Title>
					</Stack>
				</Card>
				<Card withBorder radius="md" p="md">
					<Stack spacing={4}>
						<Text size="sm" color="dimmed">
							Conversation kinds
						</Text>
						<Title order={3}>
							{numberFormat((stats.kind_distribution || []).length)}
						</Title>
					</Stack>
				</Card>
				<Card withBorder radius="md" p="md">
					<Stack spacing={4}>
						<Text size="sm" color="dimmed">
							Response samples
						</Text>
						<Title order={3}>
							{numberFormat(stats.summary?.response_samples)}
						</Title>
					</Stack>
				</Card>
			</SimpleGrid>

			<Card withBorder radius="md" p="md">
				<ChartHeader
					title="Message Volume Trend"
					note={CHART_NOTES.messageVolume}
					insight={messageVolumeInsight}
					rightSection={
						<Badge variant="light" color="green">
							{appliedGranularity === 'hour' ? 'Hourly' : 'Daily'}
						</Badge>
					}
				/>
				<ReactApexChart
					options={messageVolumeOptions}
					series={messageVolumeData.series}
					type="area"
					height={380}
				/>
			</Card>

			<Card withBorder radius="md" p="md">
				<ChartHeader
					title="Active Conversations"
					note={CHART_NOTES.activeConversations}
					insight={activeConversationInsight}
				/>
				<ReactApexChart
					options={activeConversationOptions}
					series={activeConversationData.series}
					type="line"
					height={360}
				/>
			</Card>

			<SimpleGrid
				cols={2}
				spacing="md"
				breakpoints={[{ maxWidth: 1100, cols: 1 }]}
			>
				<Card withBorder radius="md" p="md">
					<ChartHeader
						title="Top Kind Distribution"
						note={CHART_NOTES.kindDistribution}
						insight={distributionInsight}
					/>
					{distributionItems.length > 0 && (
						<Flex gap="sm" wrap="wrap" mb="sm">
							<Badge color="orange" variant="light" size="lg">
								Top messages: {conversationKindFormat(topMessageKind?.kind)}{' '}
								{Number(topMessageKind?.message_percent || 0).toFixed(1)}%
							</Badge>
							<Badge color="green" variant="light" size="lg">
								Top conversations: {conversationKindFormat(topConversationKind?.kind)}{' '}
								{Number(topConversationKind?.conversation_percent || 0).toFixed(1)}%
							</Badge>
						</Flex>
					)}
					<ReactApexChart
						options={distributionOptions}
						series={distributionSeries}
						type="bar"
						height={distributionChartHeight}
					/>
				</Card>

				<Card
					withBorder
					radius="md"
					p="md"
					sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
				>
					<ChartHeader
						title="Response Time"
						note={CHART_NOTES.responseTime}
						insight={responseInsight}
					/>
					<Box sx={{ marginTop: 'auto' }}>
						<ReactApexChart
							options={responseOptions}
							series={responseSeries}
							type="bar"
							height={420}
						/>
					</Box>
				</Card>
			</SimpleGrid>

			<Card withBorder radius="md" p="md">
				<ChartHeader
					title="Message Peak Hour Heatmap"
					note={CHART_NOTES.peakHour}
					insight={heatmapInsight}
				/>
				<ReactApexChart
					options={heatmapOptions}
					series={heatmapSeries}
					type="heatmap"
					height={420}
				/>
			</Card>
		</Stack>
	)
}

export default React.memo(CommunityStatsPage)
